import { useEffect, useState } from "react";
import { MessageCircle, ArrowRight } from "lucide-react";

const SCROLL_TRIGGER = 380;

export default function StickyCTA() {
  const [shown, setShown] = useState(false);
  const [hidden, setHidden] = useState(
    typeof document !== "undefined" && document.body.classList.contains("gd-hide-cta")
  );
  const [payInView, setPayInView] = useState(false);
  const [productLabel, setProductLabel] = useState("");
  const [productPrice, setProductPrice] = useState("");

  // Reveal after scrolling past the hero
  useEffect(() => {
    const onScroll = () => {
      setShown(window.scrollY > SCROLL_TRIGGER);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide while payment/confirmation is showing & track selected product (body dataset)
  useEffect(() => {
    if (typeof document === "undefined") return;
    const update = () => {
      setHidden(document.body.classList.contains("gd-hide-cta"));
      setProductLabel(document.body.dataset.gdProduct || "");
      setProductPrice(document.body.dataset.gdPrice || "");
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "data-gd-product", "data-gd-price"],
    });
    return () => observer.disconnect();
  }, []);

  // Hide when the actual pay button is visible in the viewport
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    let io;
    let target = null;
    const attach = () => {
      target = document.querySelector('[data-testid="submit-payment-btn"]');
      if (!target) return;
      io = new IntersectionObserver(
        (entries) => setPayInView(entries[0]?.isIntersecting === true),
        { threshold: 0.2 }
      );
      io.observe(target);
    };
    attach();
    // Retry once after mount in case button mounts later (e.g., after confirmation reset)
    const t = setTimeout(() => {
      if (!target) attach();
    }, 400);
    return () => {
      clearTimeout(t);
      if (io && target) io.unobserve(target);
      if (io) io.disconnect();
    };
  }, []);

  const visible = shown && !hidden && !payInView;

  const scrollToForm = (e) => {
    const el =
      document.querySelector('[data-testid="submit-payment-btn"]') ||
      document.querySelector(".gd-card.gd-fade-up") ||
      document.querySelector(".gd-hero-card");
    if (el) {
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const ctaTitle = productLabel ? productLabel : "Book reservation";
  const ctaSub = productPrice ? `Pay ${productPrice} securely` : "Live PNR · 60-min delivery";

  return (
    <>
      {/* MOBILE: full-width bottom bar */}
      <div
        className={`gd-cta-mobile ${visible ? "is-visible" : ""}`}
        data-testid="sticky-cta-mobile"
        aria-hidden={!visible}
      >
        <button
          className="gd-cta-mobile-primary"
          onClick={scrollToForm}
          data-testid="sticky-cta-book-btn"
        >
          <span className="gd-cta-mobile-text">
            <span className="gd-cta-mobile-title">{ctaTitle}</span>
            <span className="gd-cta-mobile-sub">{ctaSub}</span>
          </span>
          <ArrowRight size={16} />
        </button>
        <a
          href="https://wa.me/919036705707"
          target="_blank"
          rel="noreferrer"
          className="gd-cta-mobile-wa"
          aria-label="Chat on WhatsApp"
          data-testid="sticky-cta-whatsapp-mobile"
        >
          <MessageCircle size={20} />
        </a>
      </div>

      {/* DESKTOP: floating bottom-right pill */}
      <a
        href="https://wa.me/919036705707"
        target="_blank"
        rel="noreferrer"
        className={`gd-cta-desktop ${visible ? "is-visible" : ""}`}
        data-testid="sticky-cta-desktop"
        aria-hidden={!visible}
      >
        <span className="gd-cta-desktop-icon" aria-hidden>
          <MessageCircle size={18} />
        </span>
        <span className="gd-cta-desktop-text">
          <span className="gd-cta-desktop-title">Need help?</span>
          <span className="gd-cta-desktop-sub">Chat on WhatsApp</span>
        </span>
      </a>
    </>
  );
}
